import React from 'react';
import { useStore } from '../../store';

interface QidTagProps {
  /** The Roger QID (top-level key in `question_db.json`) to reveal and copy. */
  qid?: string;
  /** Optional layout override applied to the chip row. */
  className?: string;
}

/**
 * Admin-only "Show QIDs" affordance for RogerDoc-driven visit forms.
 *
 * When the Show QIDs overlay is toggled on (Header → user menu) and the current
 * user is a Roger super-user, this renders a small click-to-copy chip exposing
 * the field's Roger QID — the identifier an engineer looks up in
 * `question_db.json`. It renders nothing otherwise, so it is safe to drop into
 * any visit-form field unconditionally.
 *
 * Scope note: this surfaces ONLY the Roger QID, and only on forms powered by
 * Roger QIDs. Model-bound forms (demographics, insurance, logs) and intake have
 * no QID and intentionally get no chip.
 */
export function QidTag({ qid, className }: QidTagProps) {
  // Subscribe only to the two values that gate visibility. We derive super-user
  // from user.permissions directly (rather than the hasPermission selector) so
  // the chip re-evaluates when the current user's permissions change in place,
  // even though it lives deep in a rarely-remounted form tree. Toast actions are
  // read lazily in the handler so dozens of chips per form don't each hold a
  // subscription to them.
  const showQuestionIds = useStore(state => state.showQuestionIds);
  const isSuperUser = useStore(state =>
    (state.user?.permissions ?? []).includes('admin:super-user')
  );

  if (!showQuestionIds || !isSuperUser || !qid) return null;

  const handleCopy = async (e: React.MouseEvent) => {
    // Never let the click reach the field control sitting beneath the chip.
    e.preventDefault();
    e.stopPropagation();
    const { showSuccess, showError } = useStore.getState();
    try {
      await navigator.clipboard.writeText(qid);
      showSuccess('QID copied', `${qid} copied to clipboard`);
    } catch (err) {
      console.error('Failed to copy QID', err);
      showError('Failed to copy QID');
    }
  };

  return (
    <div className={`mb-0.5 flex ${className ?? ''}`}>
      <button
        type='button'
        onClick={handleCopy}
        title='Click to copy Roger QID'
        className='select-none rounded bg-muted px-1 py-0.5 font-mono text-[10px] leading-none text-muted-foreground/70 transition-colors hover:bg-muted/80 hover:text-foreground'
      >
        {qid}
      </button>
    </div>
  );
}
